import { Injectable } from '@angular/core';
import { JSONPath } from 'jsonpath-plus';
import { concatMap, forkJoin, from, map, Observable, of } from 'rxjs';
import { OrderService } from 'src/app/module/order/service/order.service';
import { ApproachFactoryState } from 'src/app/module/order/state/approach-factory.state';
import { EnumRefPipe } from 'src/app/shared/pipes/enum-ref.pipe';
import { SelectedSourceDataPipe } from 'src/app/shared/pipes/selected-source-data.pipe';
import { CompanyState } from 'src/app/shared/state/company.state';
import { CommonService } from 'src/app/shared/utility/common.service';
import { PropertyConfig } from '../../../shared/config/property.config';
import { EditableWhenRule, EditableWhenScope } from '../../adjustment-grid/interface/adjustment.interface';
import { OrderConfig } from '../../order/config/order.config';
import { ApproachKey, ValuationType } from '../../order/interface/order.interface';
import { ComparableService } from '../../order/service/comparable.service';
import { OrderState } from '../../order/state/order.state';
import {
  ExtendedPropertyDetail,
  PropertyAttribute,
  PropertyFieldConfig,
  PropertyImage,
  StandardStatusType,
  StructureDetail,
  UnitDetail,
} from '../../property/interface/property.interface';
import { PropertyService } from '../../property/service/property.service';
import { PropertyState } from '../../property/state/property.state';
import { ScenarioService } from '../../scenario/service/scenario.service';
import { ScenarioState } from '../../scenario/state/scenario.state';
import {
  Comparator,
  CompareContext,
  CreateValidationItemParam,
  ExternalValidationResult,
  FieldRuleFailure,
  RuleStatus,
  ValidationCondition,
  ValidationResult,
  ValidationRule,
  ValidationScope,
  ValidationType,
  ValidatorRes,
} from '../interface/validation.interface';
import { ValidationState } from '../state/validation.state';
import { resolveRuleComparableId, ruleMatchesComparable } from '../util/validation.util';

const STRICT_TYPES = [ValidationType.StrictEnumeration, ValidationType.StrictNumber, ValidationType.StrictDate];

/** Per-field rule types: evaluated against one field/level, re-checkable live per cell. */
const PER_FIELD_TYPES = [
  ValidationType.NotEmpty,
  ValidationType.AnyNotEmpty,
  ValidationType.AllNotEmpty,
  ValidationType.Range,
  ValidationType.Count,
  ValidationType.RequiredImages,
  ValidationType.RequiredImagesLabel,
  ValidationType.Compare,
  ...STRICT_TYPES,
];

/** Aggregate rule types: span multiple entities, reconciled at approach level. */
const AGGREGATE_TYPES = [ValidationType.Match, ValidationType.SelectedCompCount];

@Injectable({
  providedIn: 'root',
})
export class ValidationRuleEngineService {
  /** Guards the validationAuditTime bump against a synchronous re-entry loop. */
  private _isReconcileBumping = false;

  constructor(
    private approachFactoryState: ApproachFactoryState,
    private commonService: CommonService,
    private companyState: CompanyState,
    private orderService: OrderService,
    private orderState: OrderState,
    private propertyState: PropertyState,
    private scenarioState: ScenarioState,
    private selectedSourceDataPipe: SelectedSourceDataPipe,
    private validationState: ValidationState,
    private comparableService: ComparableService,
    private scenarioService: ScenarioService,
    private propertyService: PropertyService,
    private enumRefPipe: EnumRefPipe
  ) {
    this._initUnitReconciliation();
  }

  /**
   * This function return a validator function reference based on the validation rule type
   */
  getValidatorReference(type: ValidationType) {
    switch (type) {
      case ValidationType.NotEmpty:
        return { validator: this._ruleNotEmpty };
      case ValidationType.Count:
        return { validator: this._ruleCount };
      case ValidationType.AnyNotEmpty:
        return { validator: this._ruleAnyNotEmpty };
      case ValidationType.AllNotEmpty:
        return { validator: this._ruleAllNotEmpty };
      case ValidationType.Match:
        return { validator: this._ruleMatch };
      case ValidationType.Range:
        return { validator: this._ruleRange };
      case ValidationType.RequiredImages:
        return { validator: this._ruleRequiredImages };
      case ValidationType.RequiredImagesLabel:
        return { validator: this._ruleRequiredImagesLabel };
      case ValidationType.SelectedCompCount:
        return { validator: this._ruleSelectedCompCount };
      case ValidationType.Compare:
        return { validator: this._ruleCompare };
      case ValidationType.StrictEnumeration:
      case ValidationType.StrictNumber:
      case ValidationType.StrictDate:
      default:
        return { validator: (): ValidatorRes => ({ status: RuleStatus.success }) };
    }
  }

  /**
   * This function return a comparator function reference based on the Comparator type
   */
  getComparatorReference(type: Comparator) {
    switch (type) {
      case Comparator.equals:
        return { comparator: this._comparatorEQ };
      case Comparator.greaterthan:
        return { comparator: this._comparatorGT };
      case Comparator.notequal:
        return { comparator: this._comparatorNE };
      case Comparator.contains:
        return { comparator: this._comparatorCONTAINS };
    }
  }

  /**
   * Creates Comparator description for rule
   * @param condition
   */
  createComparatorDescription(condition: ValidationCondition) {
    let description: string = '';

    const fieldValue: any = condition.fieldDisplayValue ? condition.fieldDisplayValue : condition.fieldValue;

    switch (condition.comparator) {
      case Comparator.equals:
        description = `the ${condition.fieldName} is '${fieldValue}' `;
        break;
      case Comparator.greaterthan:
        description = `the ${condition.fieldName} is greater than ${fieldValue} `;
        break;
      case Comparator.notequal:
        description = `the ${condition.fieldName} is other than '${fieldValue}' `;
        break;
      case Comparator.contains:
        description = `the ${condition.fieldName} contains '${fieldValue}' `;
    }

    return description;
  }

  /**
   * This function is used to create description when the rule is failed
   * @param rule
   * @param status
   */
  createDescription(rule: ValidationRule, data?: any): string {
    let description: string = '';

    switch (rule.validation) {
      case ValidationType.NotEmpty:
        description = `This field is required`;

        if (rule.conditions?.length) {
          description = description.concat(' since ');

          rule.conditions.forEach((condition, index) => {
            description = description.concat(
              this.createComparatorDescription(condition),
              index < rule.conditions.length - 1 ? ' and ' : ''
            );
          });
        }
        break;
      case ValidationType.Count:
        description = `Require  minimum ${rule.min} selected comparables`;
        if (rule.max) description = `Require ${rule.min} to ${rule.max} comparables`;
        if (rule.ruleId.includes('selectedComparableList')) {
          if (Number(rule.min) && !Number(rule.max))
            description = `Require  at least ${Number(rule.min)} selected comparables`;
          if (!Number(rule.min) && Number(rule.max)) description = `Require ${Number(rule.max)} selected comparables`;
          if (Number(rule.min) && Number(rule.max))
            description = `Require ${rule.min} to ${rule.max} selected comparables`;
          if (Number(rule.min) == Number(rule.max)) description = `Require ${rule.max} selected comparables`;
        } else {
          if (rule.scope.includes('ORDER')) {
            if (Number(rule.min) && !Number(rule.max)) description = `Require  at least ${rule.min} words`;
            if (!Number(rule.min) && Number(rule.max)) description = `Require ${Number(rule.max)} words`;
            if (Number(rule.min) && Number(rule.max)) description = `Require ${rule.min} to ${rule.max} words`;
            if (Number(rule.min) == Number(rule.max)) description = `Require ${rule.max} words`;
          } else {
            if (Number(rule.min) && !Number(rule.max)) description = `Require  at least ${rule.min}`;
            if (!Number(rule.min) && Number(rule.max)) description = `Require ${Number(rule.max)}`;
            if (Number(rule.min) && Number(rule.max)) description = `Require ${rule.min} to ${rule.max}`;
            if (Number(rule.min) == Number(rule.max)) description = `Require ${rule.max}`;
          }
        }
        break;
      case ValidationType.AnyNotEmpty:
        description = `Any one of the ${rule.fieldName} fields must have a value`;
        break;
      case ValidationType.AllNotEmpty:
        description = `All of the ${rule.fieldName} fields must have a value`;
        break;
      case ValidationType.Match:
        description = `All properties should have the same values`;
        break;
      case ValidationType.Range:
        description = 'Value must be ';
        if (rule.min != null) description += `${rule.minInclusive ? 'at least' : 'greater than'} ${rule.min}`;
        if (rule.min != null && rule.max != null) description += ' and ';
        if (rule.max != null) description += `${rule.maxInclusive ? 'up to' : ' less than'} ${rule.max}`;

        break;
      case ValidationType.RequiredImages:
        description = `${(data as Array<string>).join(',')} ${(data as Array<string>).length > 1 ? 'images are' : 'image is'} required to be uploaded`;
        break;
      case ValidationType.SelectedCompCount:
        description = `Selected comp count validation failed`;
        break;
      case ValidationType.StrictEnumeration:
        description = 'Choose a valid option';
        break;
      case ValidationType.StrictNumber:
        description = 'Enter a valid number';
        break;
      case ValidationType.StrictDate:
        description = 'Choose a valid date';
        break;
    }

    return description;
  }

  /** Returns the message shown for a failed rule, preferring the config's text over the frontend's own. */
  resolveRuleDescription(rule: ValidationRule, composedMessage?: string | null): string {
    const configuredMessage = rule.description?.trim();
    if (configuredMessage) return configuredMessage;

    return composedMessage ?? this.createDescription(rule);
  }

  /**
   * This function generate validation item
   * @param rule
   * @param status
   * @param propertyId
   */
  makeValidationItem(params: CreateValidationItemParam): ValidationResult {
    const {
      rule,
      status,
      scope,
      page,
      propertyId,
      address,
      valuationType,
      scenarioId,
      structureIndex,
      unitIndex,
      description,
    } = params;

    const pathList = [...rule.path];
    if (rule.compareToField) pathList.push(rule.compareToField);

    return {
      scope: scope,
      path: pathList,
      fieldName: rule.fieldName,
      page: page,
      description: this.resolveRuleDescription(rule, description),
      status: status,
      propertyId: propertyId,
      label: address ?? '',
      valuationType: valuationType,
      scenarioId: scenarioId,
      structureIndex: structureIndex,
      unitIndex: unitIndex,
      ruleId: rule.ruleId,
      frontendRule: true,
      validationType: rule.validation,
    };
  }

  /**
   * This function generate external validation item
   * @param validation

   */
  makeExternalValidationItem(validation: ExternalValidationResult, propertyId?: string): ValidationResult {
    return {
      scope: validation.scope ?? 'ORDER',
      propertyId,
      path: validation.path ?? [],
      fieldName: validation.fieldName ?? '',
      page: validation.page ?? [],
      description: validation.description ?? '',
      status: validation.status?.toUpperCase() as RuleStatus,
      label: validation.label ?? '',
      valuationType: undefined,
      scenarioId: null,
      source: validation.source ?? null,
      ruleId: validation.ruleId ?? null,
      comparableNumber: validation.comparableNumber ?? null,
      allowOverride: validation.allowOverride ?? false,
      isOverridden: validation.isOverridden ?? false,
      userNotes: validation.userNotes ?? null,
      frontendRule: false,
    };
  }

  /**
   * This function is used to generate validation list based on the rule list
   */
  generateValidationList(externalValidation: ExternalValidationResult[]): Observable<boolean> {
    return new Observable((observer) => {
      OrderConfig.routeTreeConfig.clearValidation();

      if (this.orderState.orderDetail.isRejected) {
        this.validationState.validationAuditTime = new Date();
        observer.next(true);
        observer.complete();
      }

      from(this.validationState.validationModifiedRules ?? [])
        .pipe(
          concatMap((rule) => {
            return this.runValidation(rule);
          })
        )
        .subscribe({
          complete: () => {
            //Adding review Validations
            this.addExternalValidation(externalValidation);

            this.validationState.validationAuditTime = new Date();
            observer.next(true);
            observer.complete();
          },
        });
    });
  }

  /**
   * Resolves the validation tooltip message for a cell/field given its field list and scope.
   * Mirrors the precedence used by OrderValidatorDirective: audit error → live FE field error → required+empty.
   * Returns null when there is no validation issue.
   */
  getValidationMessage(
    fieldList: string[],
    scope: ValidationScope,
    propertyId?: string,
    structureIndex?: number,
    unitIndex?: number
  ): string | null {
    const messages: string[] = [];

    this._getMatchingAuditRules(fieldList, scope, propertyId, structureIndex, unitIndex).forEach((rule) => {
      if (rule.description) messages.push(rule.description);
    });

    // Live FE re-check for fields not yet reflected by an audit item.
    fieldList.forEach((field) => {
      this.evaluateFieldRules(field, scope, propertyId, structureIndex, unitIndex).forEach((failure) =>
        messages.push(failure.message)
      );
    });

    if (!messages.length) return null;

    // The re-run hint lives on the BE-error badge's own tooltip, not in the merged field message.
    return [...new Set(messages)].join(' • ');
  }

  /** Whether a field carries an unresolved backend (REVIEW) validation error that needs a re-run to verify. */
  hasBackendValidationError(
    fieldList: string[],
    scope: ValidationScope,
    propertyId?: string,
    structureIndex?: number,
    unitIndex?: number
  ): boolean {
    return this._getMatchingAuditRules(fieldList, scope, propertyId, structureIndex, unitIndex).some(
      (rule) => rule.frontendRule !== true
    );
  }

  /**
   * This function is used to execute a rule on different scope
   * @param rule
   */
  runValidation(rule: ValidationRule): Observable<boolean> {
    const validationObrArr: Observable<boolean>[] = [];

    if (rule.validation === ValidationType.SelectedCompCount) {
      validationObrArr.push(this.validateSelectedCompCountScope(rule));
    } else if (STRICT_TYPES.includes(rule.validation)) {
      if (rule.scope.includes('SUBJECT')) validationObrArr.push(this.validateSubjectStrictScope(rule));
      if (rule.scope.includes('COMPARABLE')) validationObrArr.push(this.validateComparableStrictScope(rule));
    } else if (this._isNonScopeSpecificRules(rule.validation)) {
      validationObrArr.push(this.validateNonSpecificRulesScope(rule));
    } else {
      if (rule.scope.includes('ORDER')) {
        validationObrArr.push(this.validateOrderScope(rule));
      }

      if (rule.scope.includes('SCENARIO')) {
        validationObrArr.push(this.validateScenarioScope(rule));
      }

      if (rule.scope.includes('SUBJECT')) {
        validationObrArr.push(this.validateSubjectScope(rule));
      }

      if (rule.scope.includes('COMPARABLE')) {
        validationObrArr.push(this.validateComparableScope(rule));
      }
    }

    return forkJoin(validationObrArr).pipe(map(() => true));
  }

  /**
   *
   * @param rule
   * @returns
   */
  validateOrderScope(rule: ValidationRule): Observable<boolean> {
    if (!this._isOrderFieldEditable(rule.path[0], this.orderState.orderDetail)) return of(true);

    const validatorRes = this.getValidatorReference(rule.validation).validator(rule, this.orderState.orderDetail);

    if (validatorRes.status != RuleStatus.success) {
      const item: ValidationResult = this.makeValidationItem({
        rule: rule,
        status: validatorRes.status,
        scope: 'ORDER',
        page: rule.page,
        description: validatorRes.message,
      });

      //Adding validation to route
      rule.page.forEach((page) => {
        OrderConfig.routeTreeConfig.addValidationToRoute(page, item);
      });
    }

    return of(true);
  }

  /**
   *
   * @param rule
   * @returns
   */
  validateScenarioScope(rule: ValidationRule): Observable<boolean> {
    this.scenarioService.getSelectedScenarioIds().forEach((id) => {
      const scenario = this.scenarioState.scenarioMap?.[id];
      if (!this._isOrderFieldEditable(rule.path[0], scenario)) return;

      const validatorRes = this.getValidatorReference(rule.validation).validator(rule, scenario);

      if (validatorRes.status != RuleStatus.success) {
        const item: ValidationResult = this.makeValidationItem({
          rule: rule,
          status: validatorRes.status,
          scope: 'SCENARIO',
          page: rule.page,
          scenarioId: id,
          description: validatorRes.message,
        });

        //Adding validation to route
        rule.page.forEach((page) => {
          OrderConfig.routeTreeConfig.addValidationToRoute(page, item);
        });
      }
    });

    return of(true);
  }

  /**
   *
   * @param rule
   * @returns
   */
  validateSubjectScope(rule: ValidationRule): Observable<boolean> {
    return this.propertyService.getSubjectProperties({ isSelected: true }).pipe(
      map((subjects) => {
        subjects.forEach((property) => {
          const subjectPages = this._subjectPagesForRule(rule);

          if (!subjectPages.length || (rule.scenarioId ? rule.scenarioId != property?.scenarioId : false)) return;

          const baseParams = {
            rule,
            scope: 'SUBJECT' as const,
            page: subjectPages,
            propertyId: property?.id,
            address: this.selectedSourceDataPipe.transform(property?.['propertyStreetAddress']),
            valuationType: undefined,
            scenarioId: property?.scenarioId,
          };

          this._forEachAttributeLevel(property, rule.path[0], ({ levelData, sIdx, uIdx }) => {
            // Skip validation entirely for fields that are not editable in this context.
            if (
              !this._isFieldEditableForProperty(
                rule.path[0] as PropertyAttribute,
                property,
                'SUBJECT',
                sIdx ?? 0,
                uIdx ?? 0
              )
            )
              return;

            const validatorRes = this.getValidatorReference(rule.validation).validator(rule, levelData, {
              property,
              structureIndex: sIdx,
              unitIndex: uIdx,
            });
            if (validatorRes.status != RuleStatus.success) {
              const item = this.makeValidationItem({
                ...baseParams,
                status: validatorRes.status,
                description: validatorRes.message,
                structureIndex: sIdx,
                unitIndex: uIdx,
              });
              subjectPages.forEach((page) => OrderConfig.routeTreeConfig.addValidationToRoute(page, item));
            }
          });
        });
        return true;
      })
    );
  }

  /**
   *Execute comparable scope validation for selected comparables of each selected scenario
   * @param rule
   * @returns
   */
  validateComparableScope(rule: ValidationRule): Observable<boolean> {
    const approachTypeConfig = OrderConfig.approachTypeConfig;

    const validationObrArr: Observable<any>[] = [];

    this.scenarioService.getSelectedScenarioIds().forEach((id) => {
      validationObrArr.push(this.comparableService.getSelectedComparables({ scenarioId: id }));
    });

    return forkJoin(validationObrArr).pipe(
      map((compRes: Array<ExtendedPropertyDetail[]>) => {
        compRes.forEach((selectedComps) => {
          (Object.keys(approachTypeConfig) as Array<ApproachKey>).forEach((key) => {
            if (this.orderState.orderSetting?.[key]?.enabled) {
              const approachComps = selectedComps.filter(
                (comp) => approachTypeConfig[key].valuationType == comp.valuationType
              );

              approachComps?.forEach((comparable) => {
                if (
                  rule.valuationType?.length &&
                  comparable.valuationType &&
                  rule.valuationType.includes(comparable.valuationType)
                ) {
                  const approachPages = rule.page.filter((p) => p.includes(approachTypeConfig[key].page)) ?? [];

                  if (!approachPages?.length || (rule.scenarioId ? rule.scenarioId != comparable?.scenarioId : false))
                    return;

                  const baseParams = {
                    rule,
                    scope: 'COMPARABLE' as const,
                    page: approachPages,
                    propertyId: comparable.id,
                    address:
                      this.selectedSourceDataPipe.transform<string>(comparable, 'propertyStreetAddress') ?? undefined,
                    valuationType: comparable.valuationType,
                    scenarioId: comparable.scenarioId,
                  };

                  this._forEachAttributeLevel(comparable, rule.path[0], ({ levelData, sIdx, uIdx }) => {
                    // Skip validation entirely for fields that are not editable in this context.
                    if (
                      !this._isFieldEditableForProperty(
                        rule.path[0] as PropertyAttribute,
                        comparable,
                        'COMPARABLE',
                        sIdx ?? 0,
                        uIdx ?? 0
                      )
                    )
                      return;

                    const validatorRes = this.getValidatorReference(rule.validation).validator(rule, levelData, {
                      property: comparable,
                      structureIndex: sIdx,
                      unitIndex: uIdx,
                    });
                    if (validatorRes.status != RuleStatus.success) {
                      const item = this.makeValidationItem({
                        ...baseParams,
                        status: validatorRes.status,
                        description: validatorRes.message,
                        structureIndex: sIdx,
                        unitIndex: uIdx,
                      });
                      approachPages.forEach((page) => OrderConfig.routeTreeConfig.addValidationToRoute(page, item));
                    }
                  });
                }
              });
            }
          });
        });

        return true;
      })
    );
  }

  validateSubjectStrictScope(rule: ValidationRule): Observable<boolean> {
    return this.propertyService.getSubjectProperties({ isSelected: true }).pipe(
      map((subjects) => {
        subjects.forEach((property) => {
          const subjectPages = this._subjectPagesForRule(rule);
          if (!subjectPages.length) return;

          const field = rule.path[0] as PropertyAttribute;
          const baseParams = {
            rule,
            scope: 'SUBJECT' as const,
            page: subjectPages,
            propertyId: property?.id,
            address: this.selectedSourceDataPipe.transform<string>(property, 'propertyStreetAddress') ?? undefined,
            valuationType: undefined,
            scenarioId: property?.scenarioId,
          };

          this._forEachAttributeLevel(property, rule.path[0], ({ sIdx, uIdx }) => {
            const s = sIdx ?? 0;
            const u = uIdx ?? 0;
            if (!this._isFieldEditableForProperty(field, property, 'SUBJECT', s, u)) return;
            const res = this._evaluateStrictRule(rule, property, true, s, u);
            if (res) {
              const item = this.makeValidationItem({
                ...baseParams,
                status: RuleStatus.error,
                description: res,
                structureIndex: sIdx,
                unitIndex: uIdx,
              });
              subjectPages.forEach((p) => OrderConfig.routeTreeConfig.addValidationToRoute(p, item));
            }
          });
        });
        return true;
      })
    );
  }

  validateComparableStrictScope(rule: ValidationRule): Observable<boolean> {
    const approachTypeConfig = OrderConfig.approachTypeConfig;
    const validationObrArr: Observable<any>[] = [];

    this.scenarioService.getSelectedScenarioIds().forEach((id) => {
      validationObrArr.push(this.comparableService.getSelectedComparables({ scenarioId: id }));
    });

    return forkJoin(validationObrArr).pipe(
      map((compRes: Array<ExtendedPropertyDetail[]>) => {
        compRes.forEach((selectedComps) => {
          (Object.keys(approachTypeConfig) as Array<ApproachKey>).forEach((key) => {
            if (!this.orderState.orderSetting?.[key]?.enabled) return;

            const approachComps = selectedComps.filter(
              (comp) => approachTypeConfig[key].valuationType == comp.valuationType
            );

            approachComps.forEach((comparable) => {
              if (
                rule.valuationType?.length &&
                comparable.valuationType &&
                !rule.valuationType.includes(comparable.valuationType)
              )
                return;

              const approachPages = rule.page.filter((p) => p.includes(approachTypeConfig[key].page));
              if (!approachPages.length || (rule.scenarioId ? rule.scenarioId != comparable?.scenarioId : false))
                return;

              const field = rule.path[0] as PropertyAttribute;
              const baseParams = {
                rule,
                scope: 'COMPARABLE' as const,
                page: approachPages,
                propertyId: comparable.id,
                address:
                  this.selectedSourceDataPipe.transform<string>(comparable, 'propertyStreetAddress') ?? undefined,
                valuationType: comparable.valuationType,
                scenarioId: comparable.scenarioId,
              };

              this._forEachAttributeLevel(comparable, rule.path[0], ({ sIdx, uIdx }) => {
                const s = sIdx ?? 0;
                const u = uIdx ?? 0;
                if (!this._isFieldEditableForProperty(field, comparable, 'COMPARABLE', s, u)) return;
                const res = this._evaluateStrictRule(rule, comparable, false, s, u);
                if (res) {
                  const item = this.makeValidationItem({
                    ...baseParams,
                    status: RuleStatus.error,
                    description: res,
                    structureIndex: sIdx,
                    unitIndex: uIdx,
                  });
                  approachPages.forEach((p) => OrderConfig.routeTreeConfig.addValidationToRoute(p, item));
                }
              });
            });
          });
        });
        return true;
      })
    );
  }

  /**
   *
   */
  validateNonSpecificRulesScope(rule: ValidationRule): Observable<boolean> {
    let validatorRes;

    const approachTypeConfig = OrderConfig.approachTypeConfig;

    const validationObrArr: Observable<any>[] = [];

    this.scenarioService.getSelectedScenarioIds().forEach((id) => {
      validationObrArr.push(this.comparableService.getSelectedComparables({ scenarioId: id }));
    });

    return forkJoin(validationObrArr).pipe(
      map((compRes: Array<ExtendedPropertyDetail[]>) => {
        compRes.forEach((selectedComps) => {
          (Object.keys(approachTypeConfig) as Array<ApproachKey>).forEach((key) => {
            if (this.orderState.orderSetting?.[key]?.enabled) {
              const approachComps = selectedComps.filter(
                (comp) => approachTypeConfig[key].valuationType == comp.valuationType
              );

              validatorRes = this.getValidatorReference(rule.validation).validator(rule, approachComps);

              const approachPages = rule.page.filter((p) => p.includes(approachTypeConfig[key].page)) ?? [];

              if (validatorRes.status != RuleStatus.success && approachPages?.length) {
                const item: ValidationResult = this.makeValidationItem({
                  rule: rule,
                  status: validatorRes.status,
                  scope: 'COMPARABLE',
                  page: approachPages,
                  valuationType: approachTypeConfig[key].valuationType,
                  description: validatorRes.message,
                });

                //Adding validation to route
                approachPages.forEach((page) => {
                  OrderConfig.routeTreeConfig.addValidationToRoute(page, item);
                });
              }
            }
          });
        });

        return true;
      })
    );
  }

  /**
   * Validates selected comp counts (total, listing, sold) for each approach and scenario.
   * Fetches selected comps per scenario and categorizes by StandardStatus.
   * Produces separate ValidationResult items for each sub-check failure.
   */
  validateSelectedCompCountScope(rule: ValidationRule): Observable<boolean> {
    const approachTypeConfig = OrderConfig.approachTypeConfig;
    const validationObrArr: Observable<any>[] = [];

    this.scenarioService.getSelectedScenarioIds().forEach((id) => {
      validationObrArr.push(this.comparableService.getSelectedComparables({ scenarioId: id }));
    });

    return forkJoin(validationObrArr).pipe(
      map((compRes: Array<ExtendedPropertyDetail[]>) => {
        compRes.forEach((selectedComps) => {
          (Object.keys(approachTypeConfig) as Array<ApproachKey>).forEach((key) => {
            if (!this.orderState.orderSetting?.[key]?.enabled) return;
            if (rule.valuationType?.length && !rule.valuationType.includes(approachTypeConfig[key].valuationType))
              return;

            const approachComps = selectedComps.filter(
              (comp) => approachTypeConfig[key].valuationType == comp.valuationType
            );
            const approachPages = rule.page.filter((p) => p.includes(approachTypeConfig[key].page)) ?? [];
            if (!approachPages?.length) return;

            const items = this._evaluateSelectedCompCount(
              rule,
              approachComps,
              approachTypeConfig[key].valuationType,
              approachPages
            );
            items.forEach((item) =>
              approachPages.forEach((page) => OrderConfig.routeTreeConfig.addValidationToRoute(page, item))
            );
          });
        });

        return true;
      })
    );
  }

  /**
   * Evaluates every per-field rule for `field` against its current value.
   */
  evaluateFieldRules(
    field: string,
    scope: ValidationScope,
    propertyId?: string,
    structureIndex = 0,
    unitIndex = 0,
    useDraft = false
  ): FieldRuleFailure[] {
    const rules = this._perFieldRulesForScope(scope, field);
    if (!rules.length) return [];

    let orderOrScenarioData: any = null;
    let property: ExtendedPropertyDetail | null | undefined;
    if (scope === 'ORDER') orderOrScenarioData = this.orderState.orderDetail;
    else if (scope === 'SCENARIO') orderOrScenarioData = this.scenarioState.activeScenario;
    else {
      property = this._resolveValidationProperty(scope, propertyId);
      if (!property) return [];
    }

    const failures: FieldRuleFailure[] = [];
    for (const rule of rules) {
      const anchor = rule.path[0] as PropertyAttribute;

      // Skip non-editable fields entirely (parity with the audit). property is only set for SUBJECT/COMPARABLE.
      if (
        property &&
        !this._isFieldEditableForProperty(anchor, property, scope as EditableWhenScope, structureIndex, unitIndex)
      )
        continue;
      if (!property && !this._isOrderFieldEditable(anchor, orderOrScenarioData)) continue;

      if (STRICT_TYPES.includes(rule.validation)) {
        const message = property
          ? this._evaluateStrictRule(rule, property, scope === 'SUBJECT', structureIndex, unitIndex)
          : null;
        if (message)
          failures.push({ rule, message: this.resolveRuleDescription(rule, message), status: RuleStatus.error });
        continue;
      }

      let data = property ? this._resolveLevelData(property, anchor, structureIndex, unitIndex) : orderOrScenarioData;
      if (!data) continue;

      if (useDraft) data = this._overlayDraftData(data, scope, propertyId, structureIndex, unitIndex);

      const res = this.getValidatorReference(rule.validation).validator(
        rule,
        data,
        property ? { property, structureIndex, unitIndex } : undefined
      );
      if (res.status !== RuleStatus.success) {
        failures.push({ rule, message: this.resolveRuleDescription(rule, res.message), status: res.status });
      }
    }
    return failures;
  }

  /**
   * Syncs one field's FE rule items in the route tree to live truth: drops resolved ones, adds newly-failing ones.
   */
  syncFieldFrontendRules(
    field: string,
    scope: ValidationScope,
    propertyId?: string,
    structureIndex?: number,
    unitIndex?: number
  ): boolean {
    const fieldRules = this._perFieldRulesForScope(scope, field);
    if (!fieldRules.length) return false;

    const fieldRuleIds = new Set(fieldRules.map((rule) => rule.ruleId));

    const match = (v: ValidationResult) =>
      v.frontendRule === true &&
      v.scope === scope &&
      v.ruleId != null &&
      fieldRuleIds.has(v.ruleId) &&
      (propertyId == null || v.propertyId === propertyId) &&
      (structureIndex == null ? v.structureIndex == null : v.structureIndex === structureIndex) &&
      (unitIndex == null ? v.unitIndex == null : v.unitIndex === unitIndex) &&
      (scope === 'COMPARABLE'
        ? v.valuationType == null || v.valuationType === this.orderState.activeValuationType
        : true) &&
      (v.scenarioId ? v.scenarioId === this.scenarioState.activeScenario?.id : true);

    const targets = this._hasAuditRun()
      ? this.evaluateFieldRules(field, scope, propertyId, structureIndex ?? 0, unitIndex ?? 0).map((failure) => ({
          item: this._buildFrontendItem(failure, scope, propertyId, structureIndex, unitIndex),
          pages: this._frontendPagesForScope(failure.rule, scope),
        }))
      : [];

    return this._reconcileTreeItems(match, targets);
  }

  /**
   * Reconciles fields whose validation depends on `editedField` via a rule condition or an
   * `enabledWhen`/`editableWhen` gate. So a cell that becomes editable/conditioned by another
   * field's change updates the tree (and panel) live.
   */
  syncDependentFieldRules(
    editedField: string,
    scope: ValidationScope,
    propertyId?: string,
    structureIndex?: number,
    unitIndex?: number
  ): boolean {
    if (!this._hasAuditRun()) return false;

    const fieldConfig = this.companyState.getPropertyFieldConfig(this.orderState.orderDetail?.reportType) ?? {};
    const dependents = new Set<string>();

    for (const rule of this.validationState.validationModifiedRules ?? []) {
      const field = rule.path[0];
      if (!field || field === editedField || !PER_FIELD_TYPES.includes(rule.validation)) continue;

      const dependsViaCondition = rule.conditions?.some((c) => c.path === editedField);
      const gateRules = [
        fieldConfig[field as PropertyAttribute]?.enabledWhen,
        fieldConfig[field as PropertyAttribute]?.editableWhen,
      ];
      const dependsViaEditable = gateRules.some((gateRule) =>
        gateRule ? this.commonService.getEditableWhenFields(gateRule).includes(editedField) : false
      );
      // COMPARE rules read a second field (compareToField); editing it must re-evaluate the anchor.
      const dependsViaCompareTo = rule.validation === ValidationType.Compare && rule.compareToField === editedField;

      if (dependsViaCondition || dependsViaEditable || dependsViaCompareTo) dependents.add(field);
    }

    let changed = false;
    dependents.forEach((field) => {
      if (this.syncFieldFrontendRules(field, scope, propertyId, structureIndex, unitIndex)) changed = true;
    });
    return changed;
  }

  /**
   * Syncs aggregate FE rules (MATCH, SELECTED_COMP_COUNT) for the active approach.
   * Optional `editedField` limits work to rules that read that field.
   */
  reconcileAggregateFrontendRules(editedField?: string): boolean {
    if (!this._hasAuditRun()) return false;

    const rules = (this.validationState.validationModifiedRules ?? []).filter(
      (rule) =>
        AGGREGATE_TYPES.includes(rule.validation) &&
        (editedField ? rule.path.includes(editedField) : true) &&
        (rule.valuationType?.includes(this.orderState.activeValuationType as ValuationType) ?? true)
    );
    if (!rules.length) return false;

    let changed = false;
    for (const rule of rules) {
      const match = (v: ValidationResult) =>
        v.frontendRule === true &&
        v.ruleId === rule.ruleId &&
        (v.valuationType == null || v.valuationType === this.orderState.activeValuationType) &&
        (v.scenarioId ? v.scenarioId === this.scenarioState.activeScenario?.id : true);
      if (this._reconcileTreeItems(match, this._evaluateAggregateRule(rule))) changed = true;
    }
    return changed;
  }

  /**
   * Writes per-field FE rule items for a comp added after an audit, so its cells get the same
   * error styling without a full re-audit.
   */
  reconcileComparableFrontendRules(propertyId: string): boolean {
    if (!this._hasAuditRun()) return false;

    const comparable = this.approachFactoryState.selectedComparableMap[propertyId];
    if (!comparable) return false;

    const fields = new Set(
      this._perFieldRulesForScope('COMPARABLE')
        .map((rule) => rule.path[0])
        .filter((field): field is string => !!field)
    );
    if (!fields.size) return false;

    let changed = false;
    fields.forEach((field) => {
      this._forEachAttributeLevel(comparable, field, ({ sIdx, uIdx }) => {
        if (this.syncFieldFrontendRules(field, 'COMPARABLE', propertyId, sIdx, uIdx)) changed = true;
      });
    });
    return changed;
  }

  /** Reconciles FE rules after a comp selection changes: aggregates always, plus per-field rules for a newly added comp. */
  reconcileAfterComparableChange(addedPropertyId?: string): boolean {
    let changed = this.reconcileAggregateFrontendRules();
    if (addedPropertyId && this.reconcileComparableFrontendRules(addedPropertyId)) changed = true;
    return changed;
  }

  /**
   * Writes per-field FE rule items for the subject's structure/unit levels, so unit cells added
   * after an audit (e.g. a PropertySubType upgrade) get the same error styling without a re-audit.
   * Similar to reconcileComparableFrontendRules.
   */
  reconcileSubjectFrontendRules(): boolean {
    if (!this._hasAuditRun()) return false;

    const subject = this.propertyState.subjectPropertyData;
    if (!subject) return false;

    const fields = new Set(
      this._perFieldRulesForScope('SUBJECT')
        .map((rule) => rule.path[0])
        .filter((field): field is string => !!field)
    );
    if (!fields.size) return false;

    let changed = false;
    fields.forEach((field) => {
      this._forEachAttributeLevel(subject, field, ({ sIdx, uIdx }) => {
        if (this.syncFieldFrontendRules(field, 'SUBJECT', undefined, sIdx, uIdx)) changed = true;
      });
    });
    return changed;
  }

  /** @description Return custom attributes validation rules */
  getCustomAttrValidationRule() {
    const customRule: ValidationRule[] = [];

    const approachTypeConfig = OrderConfig.approachTypeConfig;

    Object.keys(this.scenarioState.scenarioMap ?? {})
      ?.filter((scenarioId) => this.scenarioState.scenarioMap?.[scenarioId]?.selected)
      ?.forEach((scenarioId) => {
        (Object.keys(approachTypeConfig) as Array<ApproachKey>).forEach((key) => {
          if (this.scenarioState.scenarioMap?.[scenarioId]?.[key]?.customValueAdjustmentLineItem?.length) {
            this.scenarioState.scenarioMap?.[scenarioId]?.[key]?.customValueAdjustmentLineItem?.forEach((c) => {
              const index = customRule.findIndex((r) => r.path.includes(c.key) && r.scenarioId == scenarioId);

              if (index == -1) {
                customRule.push({
                  path: [c.key],
                  fieldName: c.displayText,
                  scope: ['SUBJECT', 'COMPARABLE'],
                  conditions: [],
                  validation: ValidationType.NotEmpty,
                  ruleId: c.key,
                  page: ['subject/detail', `${approachTypeConfig[key].page}/adjustment`],
                  custom: true,
                  valuationType: [approachTypeConfig[key].valuationType],
                  scenarioId: scenarioId,
                });
              } else if (!customRule[index].page.includes(approachTypeConfig[key].page)) {
                customRule[index] = {
                  ...customRule[index],
                  page: [...customRule[index].page, ...[`${approachTypeConfig[key].page}/adjustment`]],
                  valuationType: [
                    ...(customRule[index].valuationType as ValuationType[]),
                    ...[approachTypeConfig[key].valuationType],
                  ],
                };
              }
            });
          }
        });
      });

    return customRule;
  }

  /**
   * @description Modify validation rules while custom attributes  add/delete in the grid
   *
   */
  modifyValidationRule() {
    const baseRules = this.validationState.validationModifiedRules?.filter((r) => !r.custom);
    this.validationState.validationModifiedRules = [...(baseRules ?? []), ...this.getCustomAttrValidationRule()];
  }

  /**
   * Function that add external validation to routes
   */
  addExternalValidation(data: ExternalValidationResult[]) {
    this.validationState.unmatchedExternalValidations = [];

    data.forEach((item) => {
      const propertyId = this._pinExternalComparableId(item);

      if (item.page?.length) {
        //Adding validation to route
        item.page.forEach((page) => {
          OrderConfig.routeTreeConfig.addValidationToRoute(
            page ?? '',
            this.makeExternalValidationItem(item, propertyId)
          );
        });
      } else {
        this.validationState.unmatchedExternalValidations.push(this.makeExternalValidationItem(item, propertyId));
      }
    });
  }

  /**
   * Checks if a new comparable can be selected without exceeding any SELECTED_COMP_COUNT
   * maxima (total / listing / sold) for the given approach. Used by selection pages to
   * block checkbox selection before emitting the update.
   */
  canSelectComparable(
    newCompStatus: string | null | undefined,
    valuationType: ValuationType
  ): { allowed: boolean; message: string } {
    const rules = (this.validationState.validationModifiedRules ?? []).filter(
      (r) => r.validation === ValidationType.SelectedCompCount && r.valuationType?.includes(valuationType)
    );
    if (!rules.length) return { allowed: true, message: '' };

    const map = this.approachFactoryState.getSelectedComparableMapOnType(valuationType);
    let totalCount = 0;
    let listingCount = 0;
    let soldCount = 0;

    Object.values(map).forEach((comp) => {
      totalCount++;
      const status = this.selectedSourceDataPipe.transform(comp.StandardStatus) as string;
      if (this._isListingStatus(status)) listingCount++;
      else if (this._isSoldStatus(status)) soldCount++;
    });

    const isNewListing = this._isListingStatus(newCompStatus);
    const isNewSold = this._isSoldStatus(newCompStatus);

    for (const rule of rules) {
      if (rule.max != null && totalCount + 1 > rule.max) {
        return { allowed: false, message: `No more than ${rule.max} comparables can be selected.` };
      }
      if (rule.maxListing != null && isNewListing && listingCount + 1 > rule.maxListing) {
        return { allowed: false, message: `No more than ${rule.maxListing} listing comparables can be selected.` };
      }
      if (rule.maxSold != null && isNewSold && soldCount + 1 > rule.maxSold) {
        return { allowed: false, message: `No more than ${rule.maxSold} sold comparables can be selected.` };
      }
    }

    return { allowed: true, message: '' };
  }

  /** Pins a REVIEW item's positional comparableNumber to a propertyId, so a later comp add/remove can't drift the match. */
  private _pinExternalComparableId(item: ExternalValidationResult): string | undefined {
    if (item.scope !== 'COMPARABLE' || (item.comparableNumber ?? 0) <= 0) return undefined;

    const activePage = OrderConfig.approachTypeConfig[this.approachFactoryState.activeApproachKey]?.page;
    if (!activePage || !item.page?.some((p) => p.includes(activePage))) return undefined;

    return resolveRuleComparableId(item, this.approachFactoryState.getOrderedSelectedComparableIds());
  }

  private _perFieldRulesForScope(scope: ValidationScope, field?: string): ValidationRule[] {
    return (this.validationState.validationModifiedRules ?? []).filter(
      (rule) =>
        (field == null || rule.path.includes(field)) &&
        rule.scope.includes(scope) &&
        PER_FIELD_TYPES.includes(rule.validation) &&
        (scope === 'COMPARABLE'
          ? !!rule.valuationType?.includes(this.orderState.activeValuationType as ValuationType)
          : true)
    );
  }

  /** Audit-tree rules currently applying to a field cell (scope/property/structure/unit aware). */
  private _getMatchingAuditRules(
    fieldList: string[],
    scope: ValidationScope,
    propertyId?: string,
    structureIndex?: number,
    unitIndex?: number
  ): ValidationResult[] {
    const comparableIdOrder = scope === 'COMPARABLE' ? this.approachFactoryState.getOrderedSelectedComparableIds() : [];

    return (
      OrderConfig.routeTreeConfig
        .getValidationDetail()
        .childrenValidation?.filter(
          (rule) =>
            !rule.isOverridden &&
            rule.path.some((p) => fieldList.includes(p)) &&
            rule.scope === scope &&
            (rule.structureIndex == null || rule.structureIndex === structureIndex) &&
            (rule.unitIndex == null || rule.unitIndex === unitIndex) &&
            (scope === 'COMPARABLE'
              ? (rule.valuationType == null || rule.valuationType === this.orderState.activeValuationType) &&
                (propertyId ? ruleMatchesComparable(rule, propertyId, comparableIdOrder) : true)
              : true) &&
            (rule.scenarioId ? rule.scenarioId === this.scenarioState.activeScenario?.id : true)
        ) ?? []
    );
  }

  /** Resolves the property a SUBJECT/COMPARABLE rule applies to, with the active-property fallback. */
  private _resolveValidationProperty(
    scope: ValidationScope,
    propertyId?: string
  ): ExtendedPropertyDetail | null | undefined {
    if (scope === 'SUBJECT') return this.propertyState.subjectPropertyData;
    if (scope === 'COMPARABLE') {
      let property = propertyId ? this.approachFactoryState.selectedComparableMap[propertyId] : null;
      if (!property && propertyId && this.propertyState.activePropertyData?.id === propertyId) {
        property = this.propertyState.activePropertyData;
      }
      return property;
    }
    return null;
  }

  /** Layers the open editor's draft values onto the data for live evaluation, only if it targets this cell. */
  private _overlayDraftData(
    data: any,
    scope: ValidationScope,
    propertyId?: string,
    structureIndex = 0,
    unitIndex = 0
  ): any {
    const draft = this.validationState.editorDraft;
    if (!draft || !data) return data;
    if (draft.scope !== scope) return data;
    if ((draft.propertyId ?? null) !== (propertyId ?? null)) return data;
    if ((draft.structureIndex ?? 0) !== (structureIndex ?? 0)) return data;
    if ((draft.unitIndex ?? 0) !== (unitIndex ?? 0)) return data;

    const overlay: Record<string, unknown> = {};
    Object.entries(draft.values).forEach(([fieldKey, value]) => {
      overlay[fieldKey] = { selectedSource: 'userRecord', userRecord: value };
    });

    return { ...data, ...overlay };
  }

  /** Resolves the sub-object a field lives on (unit / structure / flat) for the given indices. */
  private _resolveLevelData(property: ExtendedPropertyDetail, path: string, sIdx = 0, uIdx = 0): any {
    switch (this._getAttributeLevel(path)) {
      case 'unit':
        return property.structures?.[sIdx]?.units?.[uIdx] ?? null;
      case 'structure':
        return property.structures?.[sIdx] ?? null;
      default:
        return property;
    }
  }

  private _hasAuditRun(): boolean {
    return this.validationState.validationAuditTime != null;
  }

  /** Pages a frontend rule's items attach to, matching the audit's page filtering. */
  private _frontendPagesForScope(rule: ValidationRule, scope: ValidationScope): string[] {
    if (scope === 'SUBJECT') return this._subjectPagesForRule(rule);
    if (scope === 'COMPARABLE') return this._activeApproachPages(rule);
    return rule.page;
  }

  /**
   * Pages a SUBJECT-scope item is stored on: its subject/basic-info pages only, so it is listed once.
   * The panel already shows it on an approach page too when the rule names one (see `validationGroup`).
   * Rules with no subject page fall back to whatever pages they name, otherwise they would show nowhere.
   */
  private _subjectPagesForRule(rule: ValidationRule): string[] {
    const pages = rule.page ?? [];
    const subjectPages = pages.filter((p) => p.includes('subject') || p.includes('basic-info'));

    return subjectPages.length ? subjectPages : pages;
  }

  private _activeApproachPages(rule: ValidationRule): string[] {
    const cfg = OrderConfig.approachTypeConfig;
    const key = (Object.keys(cfg) as ApproachKey[]).find(
      (k) => cfg[k].valuationType === this.orderState.activeValuationType
    );
    return key ? rule.page.filter((p) => p.includes(cfg[key].page)) : [];
  }

  /** Builds a frontend ValidationResult for a still-failing per-field rule, mirroring the audit's item shape. */
  private _buildFrontendItem(
    failure: FieldRuleFailure,
    scope: ValidationScope,
    propertyId?: string,
    structureIndex?: number,
    unitIndex?: number
  ): ValidationResult {
    const property = this._resolveValidationProperty(scope, propertyId);
    return this.makeValidationItem({
      rule: failure.rule,
      status: failure.status,
      scope,
      page: this._frontendPagesForScope(failure.rule, scope),
      propertyId: property?.id ?? propertyId,
      address: property
        ? (this.selectedSourceDataPipe.transform<string>(property, 'propertyStreetAddress') ?? undefined)
        : undefined,
      valuationType: scope === 'COMPARABLE' ? (this.orderState.activeValuationType as ValuationType) : undefined,
      scenarioId: scope === 'ORDER' ? undefined : (property?.scenarioId ?? this.scenarioState.activeScenario?.id),
      description: failure.message,
      structureIndex,
      unitIndex,
    });
  }

  /** Evaluates an aggregate rule (MATCH / SELECTED_COMP_COUNT) for the active approach. */
  private _evaluateAggregateRule(rule: ValidationRule): { item: ValidationResult; pages: string[] }[] {
    const valuationType = this.orderState.activeValuationType as ValuationType;
    const pages = this._activeApproachPages(rule);
    if (!pages.length) return [];

    const approachComps = Object.values(this.approachFactoryState.getSelectedComparableMapOnType(valuationType));

    if (rule.validation === ValidationType.Match) {
      if (!approachComps.length) return [];
      const res = this._ruleMatch(rule, approachComps);
      if (res.status === RuleStatus.success) return [];
      const item = this.makeValidationItem({
        rule,
        status: res.status,
        scope: 'COMPARABLE',
        page: pages,
        valuationType,
        scenarioId: this.scenarioState.activeScenario?.id,
        description: res.message ?? this.createDescription(rule),
      });
      return [{ item, pages }];
    }

    if (rule.validation === ValidationType.SelectedCompCount) {
      return this._evaluateSelectedCompCount(rule, approachComps, valuationType, pages).map((item) => ({
        item,
        pages,
      }));
    }
    return [];
  }

  /**
   * Makes the tree items matched by `match` equal `targets`. Returns false when already in sync
   * (no audit-time bump), which stops the live re-check from looping.
   */
  private _reconcileTreeItems(
    match: (v: ValidationResult) => boolean,
    targets: { item: ValidationResult; pages: string[] }[]
  ): boolean {
    const keyOf = (v: ValidationResult) =>
      `${v.ruleId ?? ''}|${v.description}|${v.structureIndex ?? ''}|${v.unitIndex ?? ''}|${v.propertyId ?? ''}`;

    /* Drop targets with no pages: they never reach the tree and would keep inSync false in a loop. */
    const addableTargets = targets.filter((t) => t.pages.length);

    const existingKeys = new Set(
      (OrderConfig.routeTreeConfig.getValidationDetail().childrenValidation ?? []).filter(match).map(keyOf)
    );
    const targetKeys = new Set(addableTargets.map((t) => keyOf(t.item)));

    const inSync = existingKeys.size === targetKeys.size && [...existingKeys].every((k) => targetKeys.has(k));
    if (inSync) return false;

    OrderConfig.routeTreeConfig.removeValidationFromRoute(match);
    addableTargets.forEach(({ item, pages }) =>
      pages.forEach((p) => OrderConfig.routeTreeConfig.addValidationToRoute(p, item))
    );

    /* The bump re-enters synchronously; the guard stops any leftover loop from overflowing the stack. */
    if (this._isReconcileBumping) return true;

    this._isReconcileBumping = true;
    try {
      this.validationState.validationAuditTime = new Date();
    } finally {
      this._isReconcileBumping = false;
    }
    return true;
  }

  /**
   * Validator function checking data of specified path is empty or not
   * @description Validating NOT_EMPTY rule
   * @param rule
   * @param data
   */
  private _ruleNotEmpty = (rule: ValidationRule, data: any, context?: CompareContext): ValidatorRes => {
    let conditionStatus: boolean = true;

    if (rule.conditions?.length) {
      conditionStatus = this._runComparator(rule.conditions, data, context);
    }

    const jsonValue: unknown = rule.scope.filter((scope) => scope == 'ORDER' || scope == 'SCENARIO')?.length
      ? JSONPath({ json: data, path: rule.path[0], wrap: true })?.[0]
      : this.selectedSourceDataPipe.transform(data[rule.path[0]]);

    if (conditionStatus && this._isFieldValueEmpty(jsonValue)) return { status: RuleStatus.error };
    else return { status: RuleStatus.success };
  };

  /**
   * Validator function checking count for specific approach type
   * @description Validating Count rule
   * @param rule
   * @param data
   */
  private _ruleCount = (rule: ValidationRule, data: any, context?: CompareContext): ValidatorRes => {
    let conditionStatus: boolean = true;

    if (rule.conditions?.length) {
      conditionStatus = this._runComparator(rule.conditions, data, context);
    }

    const jsonValue: unknown = rule.scope.filter((scope) => scope == 'ORDER' || scope == 'SCENARIO')?.length
      ? JSONPath({ json: data, path: rule.path[0], wrap: true })?.[0]
      : this.selectedSourceDataPipe.transform(data[rule.path[0]]);

    if (conditionStatus) {
      if (
        (rule.min && this.commonService.getLength(jsonValue) < rule.min) ||
        (rule.max && this.commonService.getLength(jsonValue) > rule.max) ||
        !jsonValue
      )
        return { status: RuleStatus.error };
      else return { status: RuleStatus.success };
    } else return { status: RuleStatus.success };
  };

  /**
   * Validator function checking all values are same
   * @description Validating Match rule
   * @param rule
   * @param data
   */
  private _ruleMatch = (rule: ValidationRule, data: any): ValidatorRes => {
    let conditionStatus: boolean = true;

    if (rule.conditions?.length) {
      conditionStatus = this._runComparator(rule.conditions, data);
    }

    const values: any[] = [];

    (data as ExtendedPropertyDetail[])?.forEach((comparable) => {
      if (
        rule.valuationType?.length &&
        comparable.valuationType &&
        rule.valuationType?.includes(comparable.valuationType)
      ) {
        const jsonValue: unknown = this.selectedSourceDataPipe.transform(comparable, rule.path[0] as PropertyAttribute);
        values.push(jsonValue);
      }
    });

    const subjectId = this.scenarioState.scenarioMap?.[data[0]?.scenarioId ?? '']?.property?.id; //TODO Need to make it as observable to fetch subject of corresponding scenario

    values.push(
      this.selectedSourceDataPipe.transform(
        this.orderState.propertyStore.get(subjectId ?? ''),
        rule.path[0] as PropertyAttribute
      )
    );

    if (conditionStatus) {
      const allSame = new Set(values).size === 1;
      if (!allSame) return { status: RuleStatus.error };
      else return { status: RuleStatus.success };
    } else return { status: RuleStatus.success };
  };

  /**
   * Validator function checking any of the specified path have a value
   * @description Validating ANY_NOT_EMPTY rule
   * @param rule
   * @param data
   */
  private _ruleAnyNotEmpty = (rule: ValidationRule, data: any, context?: CompareContext): ValidatorRes => {
    let conditionStatus: boolean = true;

    if (rule.conditions?.length) {
      conditionStatus = this._runComparator(rule.conditions, data, context);
    }

    //checking any of the fields have value on the specified paths
    const status = rule.path.some((p) => {
      const jsonValue: unknown = rule.scope.filter((scope) => scope == 'ORDER' || scope == 'SCENARIO')?.length
        ? JSONPath({ json: data, path: p, wrap: true })?.[0]
        : this.selectedSourceDataPipe.transform(data[p]);

      if (this._isFieldValueEmpty(jsonValue)) return false;
      else return true;
    });

    if (conditionStatus && !status) return { status: RuleStatus.error };
    else return { status: RuleStatus.success };
  };

  /**
   * Validator function checking all specified paths have a value
   * @description Validating ALL_NOT_EMPTY rule
   * @param rule
   * @param data
   */
  private _ruleAllNotEmpty = (rule: ValidationRule, data: any, context?: CompareContext): ValidatorRes => {
    let conditionStatus: boolean = true;

    if (rule.conditions?.length) {
      conditionStatus = this._runComparator(rule.conditions, data, context);
    }

    //checking all fields have value on the specified paths
    const status = rule.path.every((r) => {
      const jsonValue: unknown = rule.scope.filter((scope) => scope == 'ORDER' || scope == 'SCENARIO')?.length
        ? JSONPath({ json: data, path: r, wrap: true })?.[0]
        : this.selectedSourceDataPipe.transform(data[r]);

      if (this._isFieldValueEmpty(jsonValue)) return false;
      else return true;
    });

    if (conditionStatus && !status) return { status: RuleStatus.error };
    else return { status: RuleStatus.success };
  };

  /**
   * Validator function checking value must be in a specified range
   * @description Validating RANGE rule
   * @param rule
   * @param data
   */
  private _ruleRange = (rule: ValidationRule, data: any, context?: CompareContext): ValidatorRes => {
    let conditionStatus: boolean = true;

    if (rule.conditions?.length) {
      conditionStatus = this._runComparator(rule.conditions, data, context);
    }

    const jsonValue: unknown = rule.scope.filter((scope) => scope == 'ORDER' || scope == 'SCENARIO')?.length
      ? JSONPath({ json: data, path: rule.path[0], wrap: true })?.[0]
      : this.selectedSourceDataPipe.transform(data[rule.path[0]]);

    if (conditionStatus) {
      if (this._isFieldValueEmpty(jsonValue)) return { status: RuleStatus.success };

      if (
        (rule.min || rule.min == 0
          ? !rule.minInclusive
            ? Number(jsonValue) <= (rule.min ?? 0)
            : Number(jsonValue) < (rule.min ?? 0)
          : false) ||
        (rule.max
          ? !rule.maxInclusive
            ? Number(jsonValue) >= (rule.max ?? 100)
            : Number(jsonValue) > (rule.max ?? 100)
          : false)
      )
        return { status: RuleStatus.error };
      else return { status: RuleStatus.success };
    } else return { status: RuleStatus.success };
  };

  /**
   * Validator function checking value must be included in the list of mandatory items
   * @description Validating REQUIRED_IMAGES rule
   * @param rule
   * @param data
   */
  private _ruleRequiredImages = (rule: ValidationRule, data: any, context?: CompareContext): ValidatorRes => {
    let conditionStatus: boolean = true;

    if (rule.conditions?.length) {
      conditionStatus = this._runComparator(rule.conditions, data, context);
    }

    const jsonValue: unknown[] = [];
    const requiredImages = rule.requiredImages ?? [];

    const labels: unknown = JSONPath({ json: data, path: `${rule.path[0]}.userRecord[*].label`, wrap: true });

    if (Array.isArray(labels)) jsonValue.push(...labels.filter(Boolean));
    else if (labels) jsonValue.push(labels);

    if (conditionStatus) {
      const imagesToUpload = requiredImages
        .filter((item) => !jsonValue.includes(item))
        ?.map((item) => this.enumRefPipe.transform(item, 'imageLabels'));

      if (imagesToUpload.length)
        return { status: RuleStatus.error, message: this.createDescription(rule, imagesToUpload) };
      else return { status: RuleStatus.success };
    } else return { status: RuleStatus.success };
  };

  /**
   * Validator function checking all the uploaded images has label
   * @description Validating REQUIRED_IMAGES_LABEL rule
   * @param rule
   * @param data
   */
  private _ruleRequiredImagesLabel = (rule: ValidationRule, data: any, context?: CompareContext): ValidatorRes => {
    let conditionStatus: boolean = true;

    if (rule.conditions?.length) {
      conditionStatus = this._runComparator(rule.conditions, data, context);
    }

    const images: PropertyImage[] = JSONPath({ json: data, path: `${rule.path[0]}.userRecord`, wrap: false }) ?? [];

    if (conditionStatus) {
      const imagesWithoutLabel = images.filter((item) => !item.label);

      if (imagesWithoutLabel.length) {
        return { status: RuleStatus.error, message: 'Label is required for images' };
      } else return { status: RuleStatus.success };
    } else return { status: RuleStatus.success };
  };

  /**
   * Validator function comparing a field's value against a reference field (or literal value).
   * @description Validating COMPARE rule
   * @param rule
   * @param data
   */
  private _ruleCompare = (rule: ValidationRule, data: any, context?: CompareContext): ValidatorRes => {
    let conditionStatus: boolean = true;

    if (rule.conditions?.length) {
      conditionStatus = this._runComparator(rule.conditions, data, context);
    }

    if (!conditionStatus) return { status: RuleStatus.success };

    const config = this.companyState.getPropertyFieldConfig(this.orderState.orderDetail?.reportType);
    const isOrderOrScenarioScope = !!rule.scope.filter((scope) => scope == 'ORDER' || scope == 'SCENARIO')?.length;

    const jsonValue: unknown = isOrderOrScenarioScope
      ? JSONPath({ json: data, path: rule.path[0], wrap: true })?.[0]
      : this._resolveCompareFieldValue(rule.path[0], data, context);

    let compareToValue: unknown = rule.compareToValue ?? null;

    if (rule.compareToField)
      compareToValue = isOrderOrScenarioScope
        ? JSONPath({ json: data, path: rule.compareToField, wrap: true })?.[0]
        : this._resolveCompareFieldValue(rule.compareToField, data, context);

    if (this._isFieldValueEmpty(jsonValue) || this._isFieldValueEmpty(compareToValue))
      return { status: RuleStatus.success };

    const normalizedJsonValue = this._normalize(jsonValue, config[rule.path[0] as PropertyAttribute]);
    const normalizedCompareToValue = this._normalize(compareToValue, config[rule.compareToField as PropertyAttribute]);

    const compareToLabel =
      rule.compareToFieldName ??
      (rule.compareToField
        ? (config[rule.compareToField as PropertyAttribute]?.fieldDisplayName ?? rule.compareToField)
        : String(compareToValue));

    switch (rule.operator) {
      case 'gt':
        return normalizedJsonValue > normalizedCompareToValue
          ? { status: RuleStatus.success }
          : { status: RuleStatus.error, message: `${rule.fieldName} should be greater than ${compareToLabel}` };
      case 'gte':
        return normalizedJsonValue >= normalizedCompareToValue
          ? { status: RuleStatus.success }
          : {
              status: RuleStatus.error,
              message: `${rule.fieldName} should be greater than or equal to ${compareToLabel}`,
            };
      case 'lt':
        return normalizedJsonValue < normalizedCompareToValue
          ? { status: RuleStatus.success }
          : { status: RuleStatus.error, message: `${rule.fieldName} should be less than ${compareToLabel}` };
      case 'lte':
        return normalizedJsonValue <= normalizedCompareToValue
          ? { status: RuleStatus.success }
          : {
              status: RuleStatus.error,
              message: `${rule.fieldName} should be less than or equal to ${compareToLabel}`,
            };
      case 'eq':
        return normalizedJsonValue === normalizedCompareToValue
          ? { status: RuleStatus.success }
          : { status: RuleStatus.error, message: `${rule.fieldName} should be equal to ${compareToLabel}` };
      case 'ne':
        return normalizedJsonValue !== normalizedCompareToValue
          ? { status: RuleStatus.success }
          : { status: RuleStatus.error, message: `${rule.fieldName} should not be equal to ${compareToLabel}` };
      case 'in':
        return Array.isArray(normalizedCompareToValue) && !normalizedCompareToValue.includes(normalizedJsonValue)
          ? { status: RuleStatus.error, message: `${rule.fieldName} should be included in ${compareToLabel}` }
          : { status: RuleStatus.success };
      case 'contains':
        return this.commonService.containsIgnoreCase(normalizedJsonValue, normalizedCompareToValue)
          ? { status: RuleStatus.success }
          : { status: RuleStatus.error, message: `${rule.fieldName} should contain ${compareToLabel}` };
      default:
        return { status: RuleStatus.success };
    }
  };

  /**
   * Resolves a field's selected-source value for a COMPARE rule. When the property root is known
   * (SUBJECT/COMPARABLE scope) it resolves at the matching structure/unit level with fallback to the
   * flat property, so the anchor and reference fields can live at different nesting levels.
   */
  private _resolveCompareFieldValue(field: string, levelData: any, context?: CompareContext): unknown {
    if (context?.property)
      return this.selectedSourceDataPipe.transform(
        context.property,
        field as PropertyAttribute,
        context.structureIndex ?? 0,
        context.unitIndex ?? 0
      );
    return this.selectedSourceDataPipe.transform(levelData?.[field]);
  }

  /** Normalizes a value to a comparable primitive: epoch ms for dates, Number for numeric strings, boolean for 'true'/'false'. */
  private _normalize(value: any, fieldConfig?: PropertyFieldConfig): any {
    if (fieldConfig?.datatype === 'date' || value instanceof Date) return new Date(value as string | number).valueOf();
    if (!Array.isArray(value) && value !== '' && !isNaN(value)) return Number(value);
    if (value === 'true') return true;
    if (value === 'false') return false;
    return value;
  }

  /** Placeholder validator for SELECTED_COMP_COUNT. actual logic lives in validateSelectedCompCountScope */
  private _ruleSelectedCompCount = (): ValidatorRes => {
    return { status: RuleStatus.success };
  };

  /** Evaluates the total/listing/sold count sub-checks for one approach; returns one item per failure. */
  private _evaluateSelectedCompCount(
    rule: ValidationRule,
    approachComps: ExtendedPropertyDetail[],
    valuationType: ValuationType,
    pages: string[]
  ): ValidationResult[] {
    let listingCount = 0;
    let soldCount = 0;
    approachComps.forEach((comp) => {
      const status = this.selectedSourceDataPipe.transform(comp.StandardStatus) as string;
      if (this._isListingStatus(status)) listingCount++;
      else if (this._isSoldStatus(status)) soldCount++;
    });
    const totalCount = approachComps.length;

    const baseParams = {
      rule,
      status: RuleStatus.error,
      scope: 'SCENARIO' as const,
      page: pages,
      valuationType,
    };

    const items: ValidationResult[] = [];

    const totalFailed =
      (rule.min != null && (rule.minInclusive ? totalCount < rule.min : totalCount <= rule.min)) ||
      (rule.max != null && (rule.maxInclusive ? totalCount > rule.max : totalCount >= rule.max));
    if (totalFailed) {
      items.push(
        this.makeValidationItem({
          ...baseParams,
          description: this._buildSelectedCompCountMessage('Selected comp', rule.min, rule.max, rule.minInclusive),
        })
      );
    }

    const listingFailed =
      (rule.minListing != null && listingCount < rule.minListing) ||
      (rule.maxListing != null && listingCount > rule.maxListing);
    if (listingFailed) {
      items.push(
        this.makeValidationItem({
          ...baseParams,
          description: this._buildSelectedCompCountMessage('Listing comp', rule.minListing, rule.maxListing),
        })
      );
    }

    const soldFailed =
      (rule.minSold != null && soldCount < rule.minSold) || (rule.maxSold != null && soldCount > rule.maxSold);
    if (soldFailed) {
      items.push(
        this.makeValidationItem({
          ...baseParams,
          description: this._buildSelectedCompCountMessage('Sold comp', rule.minSold, rule.maxSold),
        })
      );
    }

    return items;
  }

  private _isListingStatus(status: string | null | undefined): boolean {
    return (
      status === StandardStatusType.active ||
      status === StandardStatusType.pending ||
      status === StandardStatusType.activeUC
    );
  }

  private _isSoldStatus(status: string | null | undefined): boolean {
    return status === StandardStatusType.sold;
  }

  /* Builds validation message for selected comp count sub-checks. */
  private _buildSelectedCompCountMessage(
    label: string,
    min?: number | null,
    max?: number | null,
    minInclusive?: boolean | null
  ): string {
    const hasMin = min != null && min > 0;
    const hasMax = max != null && max > 0;
    const minLabel = minInclusive !== false ? 'at least' : 'more than';

    if (hasMin && hasMax && min === max) return `${label} count must be exactly ${min}`;
    if (hasMin && hasMax) return `${label} count must be between ${min} and ${max}`;
    if (hasMin) return `${label} count must be ${minLabel} ${min}`;
    if (hasMax) return `${label} count must not exceed ${max}`;
    return `${label} count validation failed`;
  }

  /**
   * This function is used to execute comparator
   * @param conditions
   * @param data
   */
  private _runComparator(conditions: ValidationCondition[], data: any, context?: CompareContext) {
    const conditionStatus: boolean = conditions.every((condition) => {
      return this.getComparatorReference(condition.comparator).comparator(condition, data ?? {}, context);
    });

    return conditionStatus;
  }

  /**
   * Resolves a condition's field value for comparison.
   * ORDER/SCENARIO: read straight off the raw data via JSONPath.
   * Otherwise: use the given `data` when it has the field, else look it up from the property root
   * so that a condition can reference a field that lives at a different level than the rule's field.
   */
  private _resolveConditionValue(condition: ValidationCondition, data: any, context?: CompareContext): unknown {
    if (condition.scope == 'ORDER' || condition.scope == 'SCENARIO') {
      return JSONPath({ json: data, path: condition.path, wrap: false });
    }

    if (data?.[condition.path] !== undefined) {
      return this.selectedSourceDataPipe.transform(data[condition.path]);
    }

    if (context?.property) {
      return this.selectedSourceDataPipe.transform(
        context.property,
        condition.path as PropertyAttribute,
        context.structureIndex ?? 0,
        context.unitIndex ?? 0
      );
    }

    return null;
  }

  /**
   *Comparator function for comparing values are equals in order to perform the rule
   * @param condition
   * @param data
   */
  private _comparatorEQ = (condition: ValidationCondition, data: any, context?: CompareContext): boolean => {
    let status: boolean = false;

    const jsonValue: unknown = this._resolveConditionValue(condition, data, context);

    if (!this._isFieldValueEmpty(jsonValue)) {
      if (typeof jsonValue === 'string') {
        if (jsonValue.toString().toLowerCase() == condition.fieldValue.toString().toLowerCase()) status = true;
      } else if (typeof jsonValue === 'number') {
        if (Number(jsonValue) == Number(condition.fieldValue)) status = true;
      } else if (typeof jsonValue === 'boolean') {
        if (jsonValue == condition.fieldValue) status = true;
      }
    }

    return status;
  };

  /**
   *Comparator function for comparing values are not equal in order to perform the rule
   * @param condition
   * @param data
   */
  private _comparatorNE = (condition: ValidationCondition, data: any, context?: CompareContext): boolean => {
    let status: boolean = false;

    const jsonValue: unknown = this._resolveConditionValue(condition, data, context);

    if (!this._isFieldValueEmpty(jsonValue)) {
      if (typeof jsonValue === 'string') {
        if (jsonValue.toString().toLowerCase() != condition.fieldValue.toString().toLowerCase()) status = true;
      } else if (typeof jsonValue === 'number') {
        if (Number(jsonValue) != Number(condition.fieldValue)) status = true;
      } else if (typeof jsonValue === 'boolean') {
        if (jsonValue != condition.fieldValue) status = true;
      }
    }

    return status;
  };

  /**
   *Comparator function for checking the field value contains the condition value
   *as a case-insensitive substring in order to perform the rule
   */
  private _comparatorCONTAINS = (condition: ValidationCondition, data: any, context?: CompareContext): boolean => {
    const jsonValue: unknown = this._resolveConditionValue(condition, data, context);

    return this.commonService.containsIgnoreCase(jsonValue, condition.fieldValue);
  };

  /**
   *Comparator function for checking different conditions in order to perform the rule
   * @param condition
   * @param data
   */
  private _comparatorGT = (condition: ValidationCondition, data: any, context?: CompareContext): boolean => {
    let status: boolean = false;

    const jsonValue: unknown = this._resolveConditionValue(condition, data, context);

    if (!this._isFieldValueEmpty(jsonValue)) {
      if (Number(jsonValue) > Number(condition.fieldValue)) status = true;
    }

    return status;
  };

  private _isNonScopeSpecificRules(validationType: ValidationType) {
    return validationType == ValidationType.Match;
  }

  private _getAttributeLevel(path: string): 'unit' | 'structure' | 'flat' {
    if ((PropertyConfig.unitAttributes as ReadonlyArray<string>).includes(path)) return 'unit';
    if ((PropertyConfig.structureAttributes as ReadonlyArray<string>).includes(path)) return 'structure';
    return 'flat';
  }

  /**
   * Walks the property at the level dictated by the field's attribute kind and invokes `cb`
   * once per cell with the matching sub-object (`levelData`) and indices.
   * - unit-level → one call per `structures[sIdx].units[uIdx]`
   * - structure-level → one call per `structures[sIdx]`
   * - flat → one call with the root property and undefined indices
   */
  private _forEachAttributeLevel(
    property: ExtendedPropertyDetail,
    path: string,
    cb: (params: { levelData: any; sIdx?: number; uIdx?: number }) => void
  ): void {
    const level = this._getAttributeLevel(path);
    if (level === 'unit') {
      property.structures?.forEach((structure: StructureDetail, sIdx: number) => {
        structure.units?.forEach((unit: UnitDetail, uIdx: number) => cb({ levelData: unit, sIdx, uIdx }));
      });
    } else if (level === 'structure') {
      property.structures?.forEach((structure: StructureDetail, sIdx: number) => cb({ levelData: structure, sIdx }));
    } else {
      cb({ levelData: property });
    }
  }

  /**
   * Units add/remove (e.g. a PropertySubType change) fires gridReloadRequired$; re-sync the route
   * tree to the new unit shape so the affected property's added unit cells pick up FE rule styling
   * post-audit.
   */
  private _initUnitReconciliation(): void {
    this.propertyState.gridReloadRequired$.subscribe(({ scope, propertyId }) => {
      if (scope === 'SUBJECT') {
        this.reconcileSubjectFrontendRules();
      } else {
        this.reconcileComparableFrontendRules(propertyId);
      }
    });
  }

  /** Empty-check that handles `null | undefined | '' | whitespace string | empty array`. */
  private _isFieldValueEmpty(value: any): boolean {
    if (value == null || value === '') return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
  }

  /**
   * Returns false when a field's `enabledWhen` or `editableWhen` rule evaluates to false
   * for the given property at the given structure/unit index. Either rule failing makes the
   * field non-editable, so its validation is skipped.
   */
  private _isFieldEditableForProperty(
    field: PropertyAttribute,
    property: ExtendedPropertyDetail,
    scope: EditableWhenScope,
    structureIndex = 0,
    unitIndex = 0
  ): boolean {
    const cfg = this.companyState.getPropertyFieldConfig(this.orderState.orderDetail?.reportType)?.[field];

    return (
      this._isFieldRuleSatisfied(cfg?.enabledWhen, property, scope, structureIndex, unitIndex) &&
      this._isFieldRuleSatisfied(cfg?.editableWhen, property, scope, structureIndex, unitIndex)
    );
  }

  /**
   * Whether an ORDER or SCENARIO field's orderField `enabledWhen`/`editableWhen` rules allow editing, read against
   * that order or scenario. A rule written for a control that validates under another path is found by that path.
   * Each field a rule reads falls back to its `fallbackPath`, as the order page displays it.
   */
  private _isOrderFieldEditable(field: string, data: object | null | undefined): boolean {
    const configs = this.companyState.getOrderFieldConfig(this.orderState.reportType);
    const config = configs[field] ?? Object.values(configs).find((entry) => entry?.validationPath === field);
    const read = (path: string): unknown => (data ? JSONPath({ json: data, path, wrap: true })?.[0] : undefined);

    return this.commonService.isOrderFieldEditable(config, configs, (path) => {
      const fallbackPath = configs[path]?.fallbackPath;
      return read(path) ?? (fallbackPath ? read(fallbackPath) : undefined);
    });
  }

  /** True when the rule is absent, scoped to the other scope, or evaluates true for the property. */
  private _isFieldRuleSatisfied(
    rule: EditableWhenRule | undefined,
    property: ExtendedPropertyDetail,
    scope: EditableWhenScope,
    structureIndex = 0,
    unitIndex = 0
  ): boolean {
    if (!rule) return true;

    // A scoped rule only gates its own scope; on the other scope the field stays editable.
    // Mirrors the grid's per-scope handling in value-adjustment.service.ts.
    const ruleScope = this.commonService.getRuleScope(rule);
    if (ruleScope && ruleScope !== scope) return true;

    return this.commonService.evaluateEditableWhen(rule, property, structureIndex, unitIndex);
  }

  /** Resolves the field value and runs the applicable STRICT validator. Returns a failure message or null. */
  private _evaluateStrictRule(
    rule: ValidationRule,
    property: ExtendedPropertyDetail,
    isSubject: boolean,
    sIdx: number,
    uIdx: number
  ): string | null {
    const value = this.selectedSourceDataPipe.transform(property, rule.path[0] as PropertyAttribute, sIdx, uIdx);
    if (this._isFieldValueEmpty(value)) return null;

    switch (rule.validation) {
      case ValidationType.StrictEnumeration:
        return this._ruleStrictEnumeration(value, isSubject, rule.path[0] as PropertyAttribute);
      case ValidationType.StrictNumber:
        return this._ruleStrictNumber(value);
      case ValidationType.StrictDate:
        return this._ruleStrictDate(value);
      default:
        return null;
    }
  }

  /** Validates that value is a key in the enum list resolved from PropertyFieldConfig. */
  private _ruleStrictEnumeration(value: any, isSubject: boolean, field: PropertyAttribute): string | null {
    const cfg = this.companyState.getPropertyFieldConfig(this.orderState.orderDetail?.reportType)?.[field];
    if (!cfg) return null;

    const enumKey = isSubject ? (cfg.subjectEnumKey ?? cfg.enumKey) : cfg.enumKey;
    if (!enumKey) return null;

    const enumList = this.companyState.getEnumConfig(this.orderState.reportType)?.[enumKey];
    if (!enumList?.length) return null;

    const candidates: string[] = cfg.isMultiSelect
      ? Array.isArray(value)
        ? value.map((v) => String(v))
        : String(value)
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v)
      : [String(value)];

    const allValid = candidates.every((key) => this.enumRefPipe.matchKeyToValue(key, enumList, true) !== null);
    return allValid ? null : 'Choose a valid option';
  }

  /** Validates that value parses as a finite number. */
  private _ruleStrictNumber(value: any): string | null {
    if (typeof value === 'string' && !/^-?\d*(\.\d+)?$/.test(value)) return 'Enter a valid number';
    if (!Number.isFinite(Number(value))) return 'Enter a valid number';
    return null;
  }

  /** Validates that value parses as a real date. */
  private _ruleStrictDate(value: any): string | null {
    const parsed = new Date(value as string | number).valueOf();
    return Number.isNaN(parsed) ? 'Choose a valid date' : null;
  }
}
